import _ from 'lodash';
import { compose, get, omit, groupBy, reverse, isArray } from 'lodash/fp';
import dayjs from '../../../utils/dayjs';
import { getGBUrl } from '../../../utils/config';
import { postFetch } from '../../../utils/fetch';

export const getSettingsValue = (name) => (settings) =>
  isArray(settings)
    ? settings.find((setting) => setting.key === name)?.value
    : [];

export const getRoundPercentTypeValue = compose(
  Number,
  getSettingsValue('roundPercentType'),
);

export const getGradingType = compose(Number, getSettingsValue('gradingType'));

export const hasTimeAccommodationException = (assignment) => {
  return _.get(
    assignment,
    'studentDueDateException.exception_value.additional_time_accommodation',
    false,
  );
};

export const getDueDateException = (assignment) => {
  return _.get(
    assignment,
    'studentDueDateException.exception_value.due_date',
    null,
  );
};

export const getStudentAssignmentExceptionDate = (assignment) => {
  return _.get(
    assignment,
    'studentAssignmentException.exception_value.due_date',
    null,
  );
};

export const hasOpenedAssignments = (assignments = []) => {
  if (!Array.isArray(assignments)) {
    return false;
  }
  return assignments.some((assignment) => {
    const timeOnTask =
      assignment.timeOnTaskInMins && assignment.timeOnTaskInMins > 0;
    const isStarted = assignment.hasLaunched;
    return timeOnTask || isStarted;
  });
};

const getHoursAndMinutes = (totalMinutes) => {
  let hours = 0;
  let minutes = totalMinutes;

  if (minutes > 60) {
    hours = Math.floor(totalMinutes / 60);
    minutes = totalMinutes % 60;
  }

  return {
    hours: hours,
    minutes: minutes,
  };
};

export const getFormattedTime = (totalMinutes) => {
  if (!totalMinutes || !Number.isFinite(totalMinutes)) {
    return '';
  }

  const { hours, minutes } = getHoursAndMinutes(totalMinutes);
  let formattedTime;

  if (hours) {
    formattedTime = ''.concat(hours, 'h').concat(Math.round(minutes), 'm');
  } else {
    formattedTime = ''.concat(Math.round(minutes), 'm');
  }

  return formattedTime;
};

export const roundToTwoDecimals = (num) => {
  if (!num || !Number(num)) {
    return '0.00';
  }
  return Number(num).toFixed(2);
};

export const groupAssignmentsByCategory = compose(
  omit('undefined'),
  groupBy('gradebook_category_name'),
);

const dueDateSorter = (a, b) => {
  const aDateDueDate = a?.assignment_due_at;
  const bDateDueDate = b?.assignment_due_at;

  const aExceptionDate = getDueDateException(a);
  const bExceptionDate = getDueDateException(b);

  const aAssignmentException = getStudentAssignmentExceptionDate(a);
  const bAssignmentException = getStudentAssignmentExceptionDate(b);

  const aDate = aExceptionDate || aAssignmentException || aDateDueDate;
  const bDate = bExceptionDate || bAssignmentException || bDateDueDate;

  return +new Date(aDate) - +new Date(bDate);
};

const dateCompletedSorter = (a, b) => {
  const aDate = new Date(a?.dateCompleted);
  const bDate = new Date(b?.dateCompleted);

  return +aDate - +bDate;
};

const timeOnTaskSorter = (a, b) => {
  const aTime = a?.timeOnTaskInMins;
  const bTime = b?.timeOnTaskInMins;

  if (!aTime) return -1;
  return Number(aTime) - Number(bTime);
};

const scoreSorter = (a, b) => {
  const aScore = a?.studentGrade;
  const bScore = b?.studentGrade;

  if (a.status === 'Missed') return -1;

  return Number(aScore) - Number(bScore);
};

const nameSorter = (a, b) => a.name.localeCompare(b.name);

export const assignmentsSorter = (sortBy, assignments = []) => {
  switch (sortBy) {
    case 'assignmentName':
      return assignments.sort(nameSorter);
    case 'score':
      return assignments.sort(scoreSorter);
    case 'dueDate':
      return assignments.sort(dueDateSorter);
    case 'completed':
      return assignments.sort(dateCompletedSorter);
    case 'timeSpent':
      return assignments.sort(timeOnTaskSorter);
    default:
      return assignments;
  }
};

export const sortAssignments = (sortBy, sortOrder, assignments) => {
  const sortedAssignments = assignmentsSorter(sortBy, assignments);
  return sortOrder === 'asc' ? sortedAssignments : reverse(sortedAssignments);
};

export const getStudentAssignmentFromCategories = (categories) => {
  return categories.flatMap(({ assignments }) => assignments);
};

export const getStudentFullName = (student) => {
  const { first_name: firstName, last_name: lastName } = student ?? {};

  return `${firstName} ${lastName}`;
};

// Selector helpers
export const selectIsgData = get(
  'gradebook.individualStudentGradebookData.studentGradebook',
);
export const selectStudent = get(
  'gradebook.individualStudentGradebookData.currentStudent',
);
export const selectShowPercent = get(
  'gradebook.individualStudentGradebookData.showPercent',
);
export const selectTimeOnTaskMap = get(
  'gradebook.individualStudentGradebookData.timeOnTaskMap',
);
export const selectIndividualStudentGradebookIsLoading = get(
  'gradebook.individualStudentGradebookIsLoading',
);

export const getFormattedLatestActivityTimestamp = (date) => {
  const dateParsed = date && dayjs(date);
  // Updated format to abbreviate month and include 'at' before the time
  const formattedString =
    dateParsed && dateParsed.isValid()
      ? dateParsed.format('MMM D, YYYY [at] h:mm A')
      : '';

  return formattedString;
};

// Function to convert fetched data to CSV format and initiate download
export const handleExportCSV = async (courseId, studentDetails) => {
  try {
    const reqUrl = `${getGBUrl()}/api/v1/launches/courses/${courseId}/users/${studentDetails.studentId}/activity-logs/export`;
    const body = {
      timezoneOffset: dayjs().utcOffset(),
      sort: [{ by: 'launched_at', order: 'desc', sequence: 1 }],
    };

    const response = await postFetch(reqUrl, body);

    if (!response.ok) {
      throw new Error(`Server request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const filename = `ActivityLog_${studentDetails.lastName}_${studentDetails.firstName}_${dayjs().format('YYYYMMDD')}.csv`;

    // For IE 10+
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  } catch (error) {
    console.error('Error exporting CSV:', error);
  }
};
